import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import { Stack } from '@mui/material'
import { RefreshRounded, StopCircleRounded, UpdateRounded } from '@mui/icons-material'

import { useStores } from '@trivie/core'
import { Header } from '@trivie/ui-web/components/header'
import { NavigationTabs } from '@trivie/ui-web/components/navigation-tabs'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'
import { ExtendAssignment } from '@trivie/ui-web/components/extend-dialog'

import { SubscriptionChip } from './subscription-chip'
import { subscriptionPurchaseDetailTabs } from './tabs'

export const SubscriptionPurchaseDetailScreen: React.FunctionComponent = observer(() => {
  const { id } = useParams<{ id: string; tab?: string }>()
  const { subscriptionStore, manageUserStore } = useStores()
  const { permissions } = manageUserStore

  const confirm = useConfirm()
  const purchaseId = Number(id)

  const [showExtend, setShowExtend] = useState(false)

  useEffect(() => {
    subscriptionStore.setSelectedPurchase(null)
    subscriptionStore.getPurchase(purchaseId)
  }, [purchaseId])

  const purchase = subscriptionStore.selectedPurchase

  if (!purchase) return null

  const { isFailed, isActive, isExpiring, isExpired, displayName: userName } = purchase

  const handleReattempt = () => {
    confirm({
      title: 'Reattempt Purchase',
      dialogText: 'Are you sure you want to reattempt this purchase?',
    })
      .then(async () => {
        const success = await subscriptionStore.reattemptPurchase(purchaseId)
        if (!success) {
          confirm({
            danger: true,
            title: 'Error',
            dialogText: 'There was an issue reattempting this purchase',
            hideCancelButton: true,
          })
        } else {
          subscriptionStore.getPurchase(purchaseId)
          subscriptionStore.getPurchaseAttempts(purchase.user_id, purchaseId)
        }
      })
      .catch(() => {})
  }

  const handleEndSubscription = () => {
    confirm({
      danger: true,
      title: 'Revoke Subscription',
      dialogText:
        'Are you sure you want to revoke this subscription? This action cannot be undone.',
    })
      .then(async () => {
        const success = await subscriptionStore.revokeSubscription(purchaseId)
        if (!success) {
          confirm({
            danger: true,
            title: 'Error',
            dialogText: 'There was an issue ending this subscription',
            hideCancelButton: true,
          })
        } else {
          subscriptionStore.getPurchase(purchaseId)
        }
      })
      .catch(() => {})
  }

  return (
    <>
      <Header
        title={purchase.product_title}
        primaryAction={
          isFailed
            ? {
                text: 'Reattempt Purchase',
                startIcon: <RefreshRounded />,
                onClick: handleReattempt,
                disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_purchase'),
              }
            : !isExpired
              ? {
                  text: 'Extend Subscription',
                  startIcon: <UpdateRounded />,
                  onClick: () => setShowExtend(true),
                  disabled:
                    !purchase.expiration_date ||
                    !permissions?.permissions.some((p) => p.code === 'can_edit_purchase'),
                }
              : undefined
        }
        secondaryAction={
          isActive || isExpiring
            ? {
                text: 'Revoke Subscription',
                color: 'error',
                startIcon: <StopCircleRounded />,
                onClick: handleEndSubscription,
                disabled: !permissions?.permissions.some((p) => p.code === 'can_delete_purchase'),
              }
            : undefined
        }
      />
      <Stack direction="row" alignItems="center" gap={1} sx={{ py: 1 }}>
        <SubscriptionChip size="medium" status={purchase.status} />
      </Stack>
      <NavigationTabs tabList={subscriptionPurchaseDetailTabs} id={purchaseId} />
      {showExtend && (
        <ExtendAssignment
          title="Extend Subscription"
          open={showExtend}
          initial={purchase.expiration_date}
          onClose={() => {
            setShowExtend(false)
          }}
          onExtend={async ({ endDateTime }) => {
            const success = await subscriptionStore.extendPurchases([Number(id)], endDateTime)
            setShowExtend(false)
            if (!success) {
              confirm({
                danger: true,
                title: 'Error',
                dialogText: 'There was an issue extending this purchase',
                hideCancelButton: true,
              })
            } else {
              subscriptionStore.getPurchase(purchaseId)
              confirm({
                title: 'Success',
                dialogText: 'The purchase has been extended successfully',
                hideCancelButton: true,
              })
            }
          }}
        />
      )}
    </>
  )
})
